import React, { useState, useEffect } from "react";
import { listNcdPatients, registerConnection, extractArray } from "../data/ccProgram";
import "../styles/RegisterModal.css";

const PROGRAMS = [
  { value: "diabetes",     label: "Diabetes" },
  { value: "test_program", label: "Test Programme" },
];

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
}
const AVATAR_COLORS = ["#5b4fcf","#0d9488","#1d6fa4","#b45309","#6d28d9","#0f766e"];
function avatarColor(id = "") {
  const code = String(id).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

/* Inline icons — kept local, no external icon-library dependency. */
function SearchIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 15} height={props.size || 15} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function ProgramIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 15} height={props.size || 15} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}
function CheckIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 12} height={props.size || 12} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function RegisterModal({ onClose, onSuccess }) {
  const [patients,   setPatients]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search,     setSearch]     = useState("");
  const [selected,   setSelected]   = useState(null);
  const [programId,  setProgramId]  = useState("diabetes");
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const raw  = await listNcdPatients();
        const data = extractArray(raw);   // safe regardless of shape
        setPatients(data);
      } catch (err) {
        console.error("RegisterModal fetch error:", err);
        setFetchError("Failed to load patients. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = patients.filter((p) =>
    (p?.user_profile?.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p?.patient_id || "").toLowerCase().includes(search.toLowerCase()) ||
    (p?.user_profile?.phone_no || "").includes(search)
  );

  const handleEnroll = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      await registerConnection({ patientId: selected.patient_id, programId });
      onSuccess();
    } catch (err) {
      console.error("Enroll error:", err);
      setSaveError(err.message || "Enrollment failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cc-modal-overlay" onClick={onClose}>
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>

        <div className="cc-modal-header">
          <div className="cc-modal-header-text">
            <h2 className="cc-modal-title">Enroll Patient into Programme</h2>
            <p className="cc-modal-hint">
              Select a patient who has completed their NCD assessment and assign them to a programme.
            </p>
          </div>
          <button className="cc-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="cc-modal-body">

          {saveError  && <div className="cc-modal-error">{saveError}</div>}
          {fetchError && <div className="cc-modal-error">{fetchError}</div>}

          {/* Programme */}
          <div className="cc-modal-field">
            <label className="cc-modal-label">
              Select Programme <span className="cc-req">*</span>
            </label>
            <span className="cc-modal-sublabel">Choose the care programme</span>

            <div className="cc-program-chips">
              {PROGRAMS.map((p) => {
                const active = programId === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    className={`cc-program-chip${active ? " active" : ""}`}
                    onClick={() => setProgramId(p.value)}
                  >
                    <ProgramIcon size={14} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search */}
          <div className="cc-modal-field">
            <label className="cc-modal-label">Select Patient <span className="cc-req">*</span></label>
            <div className="cc-modal-search-wrap">
              <SearchIcon size={15} />
              <input
                className="cc-modal-search-input"
                placeholder="Search by name, mobile number or patient id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Patient list */}
          <div className="cc-modal-field">
            <label className="cc-modal-label">Eligible Patients</label>

            <div className="cc-modal-patient-list">
              {loading && (
                <div className="cc-modal-loading">
                  <div className="cc-spinner" /> Loading patients…
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="cc-modal-empty">
                  {patients.length === 0
                    ? "No unassigned patients found."
                    : "No patients match your search."}
                </div>
              )}
              {!loading && filtered.map((p) => {
                const name       = p?.user_profile?.name || "Unknown";
                const isSelected = selected?.patient_id === p.patient_id;
                const bg         = avatarColor(p.patient_id);
                const phone      = p?.user_profile?.phone_no;
                const age        = p?.user_profile?.age;
                const gender     = p?.user_profile?.gender;
                const bmi        = p?.ncd_data?.targets?.bmi   || p?.ncd_summary?.bmi;
                const goal       = p?.ncd_data?.targets?.goal  || p?.ncd_summary?.goal;

                return (
                  <div
                    key={p.patient_id}
                    className={`cc-modal-patient-row${isSelected ? " selected" : ""}`}
                    onClick={() => setSelected(p)}
                  >
                    <div className="cc-modal-patient-avatar" style={{ background: bg }}>
                      {getInitials(name)}
                    </div>
                    <div className="cc-modal-patient-info">
                      <div className="cc-modal-patient-name">{name}</div>
                      <div className="cc-modal-patient-meta">
                        {phone  ? phone : ""}
                        {age    ? ` · Age ${age}` : ""}
                        {gender ? ` · ${gender}` : ""}
                        {bmi    ? ` · BMI ${bmi}` : ""}
                        {goal   ? ` · ${goal}` : ""}
                      </div>
                    </div>

                    <span className="cc-modal-status-pill">
                      {isSelected && <CheckIcon />} Assessment Completed
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        <div className="cc-modal-footer">
          <button className="cc-modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="cc-modal-submit" onClick={handleEnroll} disabled={!selected || saving}>
            {saving ? "Enrolling…" : "Enroll Patient"}
          </button>
        </div>

      </div>
    </div>
  );
}